/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * A-1 (S711 review, built S729) -- THE ONE OWNER OF THE WORD-CREDIT BALANCE.
 *
 * Everything the shop knows about a Document Translator credit balance goes
 * through this file: how it is fetched, what makes it stale, how often it is
 * re-read, and what a screen is told when it cannot be read. The two hooks
 * (useDtCredits for the dashboard, useCartDtCredits for the cart) are thin
 * readers of it and decide nothing themselves.
 *
 * Why it exists. Before this, each screen read the balance its own way and,
 * when the read failed, each substituted a different number: zero in the cart,
 * the lifetime total on Product Details, the browser's own sum of the rows in
 * the banner. One backend fault (C-1) produced four different wrong answers on
 * three screens. The missing piece was never a better fallback -- it was the
 * difference between "the balance is zero" and "the balance is not known".
 *
 * So the owner answers with a STATE, not a number (`DtBalanceState`):
 *
 *   idle     Nothing was asked for (no Document Translator orders, signed out,
 *            or no machine to ask about). There is no balance to show.
 *   loading  The first read has not answered.
 *   known    A figure was read from the server. Zero is a real answer here.
 *   unknown  The read failed, or answered something that is not a figure.
 *            No figure travels with it, so no screen can show one.
 *
 * Each screen writes down what it does with "unknown" at the point it reads
 * it. None of them invents a number.
 *
 * Two endpoints, one owner:
 *   GET /portal/dt-credits/balance/?machine_id=<id>   (the cart)
 *     { available_words, held_words } -- the SPENDABLE figure, plus the words a
 *     translation in progress is holding (VM-1, S712).
 *   GET /portal/dt-credits/                            (the dashboard)
 *     { credit_balance, words_credited, words_used, words_purchased, invoices }
 *     -- credit_balance is the ledger total the desktop header also shows.
 *
 * Both provide the "BALANCE" tag, so one invalidation -- `refreshDtCreditBalance`,
 * dispatched on the `credits-updated` socket event and available to anything
 * else that changes the balance -- re-reads every balance on screen.
 *
 * API-PRIVACY-1 (Session 541): the ledger endpoint no longer echoes the e-mail.
 * S429 URL-fix: the ledger lives at /balance/ because the portal app's
 * dt-credits/ route shadowed it when both were mounted at api/v1/portal/.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth, CACHE_TIMES, TAG_TYPES } from "@/redux/api-config";
import type { DtOrderStatus, DtWordsJob } from "@/lib/dt-orders-merge";
import i18n from "@/i18n";

// ── Response types ───────────────────────────────────────────────────────────

/** The cart's reading: the DB ledger for one machine. */
export interface DtCreditsResponse {
  available_words: number;
  /**
   * VM-1 (S712): the part of the ledger committed to a translation in
   * progress. `available_words + held_words` is what the customer owns, which
   * is the figure Product Details and the desktop header show -- so this is
   * the difference the cart has to explain rather than leave unexplained.
   *
   * Optional, because a shop server from before S712 does not send it and an
   * absent field must read as "nothing held", not as a crash.
   */
  held_words?: number;
  /** Session 881 review, CP-2: words this customer gets by verifying; 0 if none. */
  free_words_on_verification?: number;
  /** Session 888 review, FW-1: the free-words price rule applies. */
  never_paid?: boolean;
}

export interface DtInvoiceCredit {
  id: string;
  /** Unix timestamp (seconds). */
  date: number;
  words_purchased: number;
  words_credited: number;
  words_used: number;
  document_name: string | null;
  status: DtOrderStatus;
}

/** The dashboard's reading: the account summary with its per-invoice rows. */
export interface DtCreditsSummaryResponse {
  /** Total words purchased across all DT invoices. */
  words_purchased: number;
  /** Total words ever restored after translation failures (history). */
  words_credited: number;
  /** Total words actually consumed across all translations. */
  words_used: number;
  /** The ledger balance -- the figure the desktop header shows. */
  credit_balance: number;
  /** Per-invoice breakdown. */
  invoices: DtInvoiceCredit[];
  /** Session 881 review, CP-2: jobs the customer's words covered; they have no invoice. */
  words_jobs?: DtWordsJob[];
}

// ── The state every reader answers with ─────────────────────────────────────

/**
 * "known" carries the figures; nothing else does. A screen must narrow on
 * `status` before it can read a number, so "unknown" cannot be mistaken for 0.
 */
export type DtBalanceState<Known> =
  | { status: "idle" }
  | { status: "loading" }
  | ({ status: "known"; refreshing: boolean } & Known)
  | {
      status: "unknown";
      error: string;
      retrying: boolean;
      /**
       * Session 875 review, SI-1: true when the read was refused with 401 --
       * the sign-in can no longer be renewed. The cart sends the customer
       * through sign-in instead of refusing Proceed with "still loading" on
       * every press. Optional: only the cart reads it, and an outage (any
       * other failure) leaves it false and keeps the retry.
       */
      signedOut?: boolean;
    };

export type CartDtBalance = DtBalanceState<{
  /** Words checkout will apply to this order. */
  spendable: number;
  /** Words a paid translation in progress is holding; 0 when none. */
  held: number;
  /** Words this customer gets by proving their address; 0 when none. */
  freeWordsOnVerification: number;
  /** Session 888 review, FW-1: the free-words price rule applies. */
  neverPaid: boolean;
}>;

export type AccountDtBalance = DtBalanceState<{
  /** The ledger balance (credit_balance). */
  words: number;
  /** The rest of the account reading: history, usage and invoice rows. */
  summary: DtCreditsSummaryResponse;
}>;

// ── Refresh rules ────────────────────────────────────────────────────────────

/**
 * How often an on-screen balance is re-read. Sixty seconds -- one request a
 * minute for a customer sitting on the page, against a balance that changes
 * only when a translation finishes or fails.
 *
 * N-3 / V-2 (S697): the poll exists because the browser has no event to wait
 * for on the dashboard: emit_credits_updated() addresses the DESKTOP's machine
 * group, and a browser is not in it (N-15). It is what keeps the dashboard and
 * the desktop toolbar agreeing. If the browser is ever sent that event, this
 * poll can go.
 */
export const DT_CREDITS_POLL_MS = 60_000;

/**
 * True when this build is a test run. `import.meta.env.MODE` is "test" under
 * vitest; the optional chaining is there because a consumer bundling this
 * module without Vite's define step would otherwise throw on the property.
 */
const isTestEnvironment = (): boolean =>
  (import.meta as { env?: { MODE?: string } }).env?.MODE === "test";

/**
 * The polling interval a reader passes to its query. Zero -- no poll -- when
 * nothing is being read, and under test: a timer still firing inside the suite
 * turns every "how many requests were made" assertion into a race, so the poll
 * is held by its own test instead.
 */
export const dtCreditsPollInterval = (
  skip: boolean,
  isTest: boolean = isTestEnvironment(),
): number => (skip || isTest ? 0 : DT_CREDITS_POLL_MS);

// ── Fetching ─────────────────────────────────────────────────────────────────

const BALANCE_TAG = { type: TAG_TYPES.DT_CREDITS, id: "BALANCE" } as const;

export const dtCreditsApi = createApi({
  reducerPath: "dtCreditsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.DT_CREDITS],
  // Credit balance changes on every translation — use SHORT cache (60 s).
  keepUnusedDataFor: CACHE_TIMES.SHORT,
  endpoints: (builder) => ({
    getDtCredits: builder.query<DtCreditsResponse, string>({
      // Arg is the machine_id string
      query: (machineId) =>
        `/portal/dt-credits/balance/?machine_id=${encodeURIComponent(machineId)}`,
      providesTags: (_result, _error, machineId) => [
        { type: TAG_TYPES.DT_CREDITS, id: machineId },
        BALANCE_TAG,
      ],
      keepUnusedDataFor: CACHE_TIMES.SHORT,
    }),
    getDtCreditsSummary: builder.query<DtCreditsSummaryResponse, void>({
      query: () => "/portal/dt-credits/",
      providesTags: () => [
        { type: TAG_TYPES.DT_CREDITS, id: "SUMMARY" },
        BALANCE_TAG,
      ],
      keepUnusedDataFor: CACHE_TIMES.SHORT,
    }),
  }),
});

export const { useGetDtCreditsQuery, useGetDtCreditsSummaryQuery } = dtCreditsApi;

/**
 * Re-read every balance on screen. Dispatched on the `credits-updated` socket
 * event (the cart), and the one thing to dispatch after anything else that
 * changes a balance.
 */
export const refreshDtCreditBalance = () =>
  dtCreditsApi.util.invalidateTags([BALANCE_TAG]);

// ── Reading ──────────────────────────────────────────────────────────────────

/** The parts of an RTK Query result the owner reads. */
export interface DtBalanceQueryResult<Data> {
  data?: Data;
  error?: unknown;
  isError: boolean;
  isFetching: boolean;
}

/** The server's own message where it sent one; otherwise the shared sentence. */
const errorMessage = (error: unknown): string => {
  const body = (error as { data?: { error?: unknown } } | undefined)?.data;
  return typeof body?.error === "string" && body.error
    ? body.error
    : i18n.t("dashboard.licenseDetails.dt.creditBalanceFailed");
};

const isFigure = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Turn a query result into the state a screen renders.
 *
 * The ORDER is the rule:
 *   1. Skipped            -> idle.
 *   2. Last read failed   -> unknown. RTK Query keeps the last good `data`
 *      after a failed refetch, and reading it anyway is how a stale balance
 *      became a price (V-3, S703). `error` stays set while a retry is in
 *      flight, so the retry is shown as a retry, not as the old figure.
 *   3. A figure arrived   -> known.
 *   4. Something arrived that is not a figure -> unknown.
 *   5. Nothing yet        -> loading.
 */
export function readDtBalance<Data, Known>(
  query: DtBalanceQueryResult<Data>,
  skip: boolean,
  pick: (data: Data) => Known | null,
): DtBalanceState<Known> {
  if (skip) return { status: "idle" };
  if (query.isError || query.error !== undefined) {
    return {
      status: "unknown",
      error: errorMessage(query.error),
      retrying: query.isFetching,
      signedOut:
        (query.error as { status?: unknown } | undefined)?.status === 401,
    };
  }
  if (query.data !== undefined && query.data !== null) {
    const known = pick(query.data);
    if (known === null) {
      return {
        status: "unknown",
        error: errorMessage(undefined),
        retrying: query.isFetching,
      };
    }
    return { status: "known", refreshing: query.isFetching, ...known };
  }
  return query.isFetching ? { status: "loading" } : {
    status: "unknown",
    error: errorMessage(undefined),
    retrying: false,
  };
}

/** The cart's figures, or null when the answer carries none. */
export const pickCartBalance = (
  data: DtCreditsResponse,
): {
  spendable: number;
  held: number;
  freeWordsOnVerification: number;
  neverPaid: boolean;
} | null =>
  isFigure(data.available_words)
    ? {
        spendable: data.available_words,
        // Absent means nothing is held (a server from before S712).
        held: isFigure(data.held_words) ? data.held_words : 0,
        freeWordsOnVerification: isFigure(data.free_words_on_verification)
          ? data.free_words_on_verification
          : 0,
        neverPaid: data.never_paid === true,
      }
    : null;

/** The dashboard's figures, or null when the answer carries none. */
export const pickAccountBalance = (
  data: DtCreditsSummaryResponse,
): { words: number; summary: DtCreditsSummaryResponse } | null =>
  isFigure(data.credit_balance)
    ? { words: data.credit_balance, summary: data }
    : null;

/**
 * The DtCreditBanner props for a balance state, so the three screens that show
 * the banner cannot disagree about it. `creditBalance` is read by the banner
 * only in the known state; in every other state the banner shows a skeleton,
 * the could-not-load message, or nothing.
 */
export function dtBannerState<Known>(
  balance: DtBalanceState<Known>,
  figure: (known: Known) => number,
): {
  creditBalance: number;
  isLoading: boolean;
  isRefreshing: boolean;
  isError: boolean;
} {
  switch (balance.status) {
    case "known":
      return {
        creditBalance: figure(balance),
        isLoading: false,
        isRefreshing: balance.refreshing,
        isError: false,
      };
    case "unknown":
      return {
        creditBalance: 0,
        isLoading: false,
        isRefreshing: balance.retrying,
        isError: true,
      };
    case "loading":
      return { creditBalance: 0, isLoading: true, isRefreshing: false, isError: false };
    default:
      return { creditBalance: 0, isLoading: false, isRefreshing: false, isError: false };
  }
}
